// src/components/intake/bodies/PlaidPanel.jsx
import React, { useCallback, useEffect, useRef } from "react";
import { Button } from "../../ui";
import PlaidConsentRow from "../../plaid/PlaidConsentRow";
import { usePlaidOffer } from "../../plaid/usePlaidOffer";
import { GENERIC_ERROR } from "../../onboarding/onboardingCopy";
import { filled, readSuggestions } from "./plaidSuggest";
import { intakePath } from "../intakeNav";
import {
  PLAID_BUTTON,
  PLAID_ERROR,
  PLAID_HEADING,
  PLAID_LEDE,
  PLAID_UNSURE,
} from "../intakeCopy";

// Q5/Q6's "Fill with Plaid" panel, with no plan gate (spec § Plaid as an
// offer, decision 22); the link flow is the shared one (plaid/usePlaidOffer).
// `onChange` is the page's state setter.
export default function PlaidPanel({
  page,
  plaid,
  onChange,
  onPlaidChange,
  idPrefix,
}) {
  // The page's onChange is a new function every render; held in a ref so the
  // suggestions read below runs on load and after a link, not on every render.
  const change = useRef(onChange);
  change.current = onChange;
  const suggest = useCallback(async () => {
    const res = await readSuggestions(page.key);
    if (res.error) return "plaid";
    change.current((draft) => filled(draft, res.suggestions));
    return null;
  }, [page.key]);
  // One suggestions read per mount: on load when already linked, or after a
  // link made here (which flips the status and must not read a second time).
  const read = useRef(false);
  const offer = usePlaidOffer({
    redirectPath: intakePath(page.key),
    consent: plaid.consent,
    onLinked: () => {
      read.current = true;
      return suggest();
    },
    onStatus: onPlaidChange,
  });
  const { setFailed } = offer;
  useEffect(() => {
    if (plaid.linked && plaid.consent && !read.current) {
      read.current = true;
      suggest().then(setFailed);
    }
  }, [plaid.linked, plaid.consent, suggest, setFailed]);

  return (
    <section
      aria-labelledby={`${idPrefix}-plaid-h`}
      className="tw-grid tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-divider tw-bg-surface tw-p-6"
    >
      <h2 id={`${idPrefix}-plaid-h`} className="tw-m-0 tw-text-lg tw-font-bold tw-text-text">
        {PLAID_HEADING}
      </h2>
      <p className="tw-m-0 tw-text-sm tw-text-text-muted">{PLAID_LEDE}</p>
      <p className="tw-m-0 tw-text-sm tw-text-text-muted">{PLAID_UNSURE}</p>
      <div>
        <Button variant="ghost" onClick={offer.start}>
          {PLAID_BUTTON[page.key]}
        </Button>
      </div>
      {offer.consentRow && (
        <PlaidConsentRow
          row={offer.consentRow}
          onConsent={offer.giveConsent}
          idPrefix={idPrefix}
        />
      )}
      {offer.failed && (
        <p role="alert" className="tw-m-0 tw-text-sm tw-text-danger">
          {offer.failed === "plaid" ? PLAID_ERROR : GENERIC_ERROR}
        </p>
      )}
    </section>
  );
}
